import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { fetchFeatureFlags as fetchFlags } from '@frontend/utils';
import { getMerchantApiUrl } from '../services/apollo';

export interface FeatureFlags {
  /** Gates the OpenPayd partner disclosure shown on first visit. */
  openpaydIntegration: boolean | undefined;
}

interface FeatureFlagContext {
  featureFlags: FeatureFlags;
}

/**
 * `undefined` means "not known yet" (still loading, or the request failed).
 * Consumers must treat it as off so nothing flag-gated leaks before the
 * flags resolve.
 */
const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  openpaydIntegration: undefined,
};

const FeatureFlagContext = createContext<FeatureFlagContext>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
});

const fetchFeatureFlags = async (): Promise<FeatureFlags> => {
  try {
    return await fetchFlags(getMerchantApiUrl(), DEFAULT_FEATURE_FLAGS);
  } catch (error) {
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setExtra('errorType', (error as Error)?.name);
      scope.setExtra('errorMessage', (error as Error)?.message);
      scope.setTag('error_type', 'network_error');

      Sentry.captureException(error);
    });

    return DEFAULT_FEATURE_FLAGS;
  }
};

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    DEFAULT_FEATURE_FLAGS
  );

  useEffect(() => {
    let cancelled = false;

    fetchFeatureFlags().then((flags) => {
      if (!cancelled) setFeatureFlags(flags);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ featureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = () => useContext(FeatureFlagContext);
